import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { cn } from "@/common/utils/cn";
import { MaintenanceForwardingData } from "@/features/maintenance/features/workflows/types/forwardings/maintenanceForwarding.type";
import { CarIcon } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { UpdateDiagnosticEntryValidationSchema } from "../../../../../schemas/updateDiagnosticEntry.schema";
import DiagnosticEntryUpdateObjectiveAttributesDiagnosticObjectivePieces from "./DiagnosticObjectivePieces";
import DiagnosticEntryUpdateObjectiveAttributesDiagnosticObjectiveActivities from "./DiagnosticObjectiveActivities";
import { useMemo } from "react";
import { MaintenanceForwardingsDirectiveEntryKlass } from "@/features/maintenance/features/workflows/types/forwardings/maintenanceForwardingsDirectiveEntryKlasses.type";
import { MaintenanceForwardingDirectiveEntryMap } from "@/features/maintenance/features/workflows/types/forwardings/directiveEntries/directiveEntry.type";

interface DiagnosticEntryUpdateObjectiveAttributesProps {
  forwarding: MaintenanceForwardingData<
    MaintenanceForwardingDirectiveEntryMap[MaintenanceForwardingsDirectiveEntryKlass.DiagnosticEntry]
  > | undefined;
  submit: VoidFunction;
  dataUpdatedAt?: number;
}

const DiagnosticEntryUpdateObjectiveAttributes = ({
  forwarding,
  submit,
  dataUpdatedAt,
}: DiagnosticEntryUpdateObjectiveAttributesProps) => {
  const {
    setValue,
    formState: {
      disabled: formDisabled,
    },
  } = useFormContext<UpdateDiagnosticEntryValidationSchema>();

  const objectives = useMemo(() => {
    const objectives = forwarding?.directive_entry?.objectives;

    objectives?.sort((a, b) => a.id.localeCompare(b.id));

    return objectives;
  }, [dataUpdatedAt]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-lg font-medium">
          Objetos originais
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {objectives?.map((objective, index) => (
          <Box
            key={objective.id}
            className={cn(
              'flex flex-col gap-4 p-4 rounded-lg',
              objective.does_not_need_estimate === true && 'bg-danger-op',
            )}
          >
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 flex-wrap justify-between">
              <div className="flex flex-col md:flex-row items-start md:items-center flex-wrap gap-y-2 gap-x-4">
                <div className="flex">
                  <Box className="bg-white-200 p-2 flex items-center gap-2">
                    <CarIcon strokeWidth={1.5} />

                    <p className="text-sm font-bold">
                      {objective.system.display_name}
                    </p>
                  </Box>
                </div>

                <div className="flex flex-col items-start">
                  <p className="font-bold text-base">
                    {objective.action.display_name} <span className="font-normal">em</span> {objective.part.display_name}
                  </p>

                  <p className="text-xs">
                    {objective.action.description}
                  </p>
                </div>
              </div>

              <div className="flex gap-3 ml-auto">
                {objective.does_not_need_estimate && (
                  <Button variant="neutral" className="pointer-events-none">
                    Sem orçamento
                  </Button>
                )}

                {!objective.does_not_need_estimate && (
                  <Button
                    variant="outline-error"
                    disabled={formDisabled}
                    onClick={() => {
                      setValue(`objectives_attributes.${index}.does_not_need_estimate`, true);

                      submit();
                    }}
                  >
                    Nada a ser orçado
                  </Button>
                )}

                {objective.does_not_need_estimate !== false && (
                  <Button
                    variant="outline-success"
                    disabled={formDisabled}
                    onClick={() => {
                      setValue(`objectives_attributes.${index}.does_not_need_estimate`, false);

                      submit();
                    }}
                  >
                    Orçar
                  </Button>
                )}
              </div>
            </div>

            {objective.does_not_need_estimate === false && (
              <div className="flex flex-col gap-4">
                <DiagnosticEntryUpdateObjectiveAttributesDiagnosticObjectivePieces
                  diagnosticObjective={objective}
                />

                <DiagnosticEntryUpdateObjectiveAttributesDiagnosticObjectiveActivities
                  diagnosticObjective={objective}
                />
              </div>
            )}
          </Box>
        ))}
      </div>
    </div>
  );
}

export default DiagnosticEntryUpdateObjectiveAttributes;
